/**
 * Equipment Tracker - Equipment Detail
 * @author Omid Zaeri (zaerio)
 */

import React, { useEffect, useState } from 'react';
import {
  Container, Header, SpaceBetween, Button, StatusIndicator, ColumnLayout, Box,
  Table, Modal, FormField, Select, Textarea, Link,
} from '@cloudscape-design/components';
import { getEquipmentById, acceptPolicy, getStatusReports, submitStatusReport, deleteEquipment } from '../services/api';
import { useAuth } from '../context/AuthContext';
import config from '../config';

export default function EquipmentDetail() {
  const { user, isAdmin } = useAuth();
  const [equipment, setEquipment] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({ status: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const equipmentId = window.location.pathname.split('/').pop();

  const loadData = async () => {
    try {
      const [eq, reps] = await Promise.all([
        getEquipmentById(equipmentId),
        getStatusReports(equipmentId),
      ]);
      setEquipment(eq);
      setReports(reps);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleAcceptPolicy = async () => {
    setSubmitting(true);
    try {
      await acceptPolicy(equipmentId);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitReport = async () => {
    setSubmitting(true);
    try {
      await submitStatusReport({ equipmentId, ...reportForm });
      setShowReportModal(false);
      setReportForm({ status: '', notes: '' });
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Container><p>Loading...</p></Container>;
  if (!equipment) return <Container><p>Equipment not found.</p></Container>;

  const isOwner = user?.alias === equipment.assignedTo;

  return (
    <SpaceBetween size="l">
      <Container header={
        <Header variant="h1" actions={
          isAdmin && (
            <Button variant="normal" onClick={async () => {
              if (!window.confirm(`Remove "${equipment.type}" assigned to ${equipment.assignedTo}?`)) return;
              try {
                await deleteEquipment(equipmentId);
                window.location.href = '/';
              } catch (err) { alert(err.message); }
            }}>Remove Equipment</Button>
          )
        }>{equipment.type}</Header>
      }>
        <ColumnLayout columns={3} variant="text-grid">
          <div><Box variant="awsui-key-label">Equipment ID</Box><div>{equipment.equipmentId}</div></div>
          <div><Box variant="awsui-key-label">Serial Number</Box><div>{equipment.serialNumber || '-'}</div></div>
          <div><Box variant="awsui-key-label">Assigned To</Box><div>{equipment.assignedTo}</div></div>
          <div><Box variant="awsui-key-label">Assigned Date</Box><div>{new Date(equipment.assignedDate).toLocaleDateString()}</div></div>
          <div>
            <Box variant="awsui-key-label">Status</Box>
            <StatusIndicator type={equipment.status === 'active' ? 'success' : 'warning'}>
              {equipment.status}
            </StatusIndicator>
          </div>
          <div>
            <Box variant="awsui-key-label">AUP Accepted</Box>
            <div>{equipment.policyAccepted ? `Yes (${new Date(equipment.policyAcceptedDate).toLocaleDateString()})` : 'No'}</div>
          </div>
          <div><Box variant="awsui-key-label">Next Report Due</Box><div>{equipment.nextReminderDate ? new Date(equipment.nextReminderDate).toLocaleDateString() : '-'}</div></div>
          <div><Box variant="awsui-key-label">Description</Box><div>{equipment.description || '-'}</div></div>
        </ColumnLayout>
      </Container>

      {isOwner && !equipment.policyAccepted && (
        <Container header={<Header variant="h2">Acceptable Use Policy</Header>}>
          <SpaceBetween size="m">
            <p>
              You must review and accept the{' '}
              <Link href={config.aupUrl} external>Acceptable Use Policy (US)</Link>{' '}
              before using this equipment.
            </p>
            <Button variant="primary" onClick={handleAcceptPolicy} loading={submitting}>
              I have read and accept the Acceptable Use Policy
            </Button>
          </SpaceBetween>
        </Container>
      )}

      {isOwner && equipment.policyAccepted && (
        <Button variant="primary" onClick={() => setShowReportModal(true)}>Submit Status Report</Button>
      )}

      <Table
        header={<Header variant="h2">Status Reports</Header>}
        items={reports}
        columnDefinitions={[
          { id: 'date', header: 'Date', cell: (r) => new Date(r.reportDate).toLocaleDateString() },
          { id: 'status', header: 'Status', cell: (r) => r.status },
          { id: 'notes', header: 'Notes', cell: (r) => r.notes || '-' },
        ]}
        empty={<Box textAlign="center" padding="l"><b>No status reports yet</b></Box>}
      />

      <Modal
        visible={showReportModal}
        onDismiss={() => setShowReportModal(false)}
        header="Submit Status Report"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowReportModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmitReport} loading={submitting}>Submit</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="l">
          <FormField label="Equipment Status">
            <Select
              selectedOption={reportForm.status ? { value: reportForm.status, label: reportForm.status } : null}
              onChange={({ detail }) => setReportForm({ ...reportForm, status: detail.selectedOption.value })}
              options={[
                { value: 'Working', label: 'Working' },
                { value: 'Needs Repair', label: 'Needs Repair' },
                { value: 'Lost', label: 'Lost' },
                { value: 'Stolen', label: 'Stolen' },
                { value: 'Decommissioned', label: 'Decommissioned' },
              ]}
            />
          </FormField>
          <FormField label="Notes">
            <Textarea value={reportForm.notes} onChange={({ detail }) => setReportForm({ ...reportForm, notes: detail.value })} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
